import { Badge, Descriptions, Divider, Drawer, Empty, Image, Table, Tag, Typography } from "antd"
import moment from "moment"
import { OrderLocation } from "../Orders/OrderLocation"
import { returnProperMachineTag } from "../utils/ReturnProperMachineTag"

const { Text } = Typography

const STATUS_COLOR = { 7: "green", 6: "blue", 5: "cyan", 3: "purple", 4: "blue" }

function fmt(ts) {
  return ts?.seconds ? moment.unix(ts.seconds).format("DD/MM/YYYY HH:mm") : "—"
}

function PriceTable({ rows }) {
  if (!rows?.length) return <Text type="secondary">No price data.</Text>
  return (
    <Table
      size="small"
      dataSource={rows}
      rowKey={(_, i) => i}
      pagination={false}
      columns={[
        {
          title: "Quantity",
          dataIndex: "quantity",
          render: (v) => v?.toLocaleString() || "—",
        },
        {
          title: "Price (€)",
          dataIndex: "price",
          align: "right",
          render: (v) => (v != null ? `€${v}` : "—"),
        },
        {
          title: "m²",
          dataIndex: "m2",
          align: "right",
          render: (v) => (v != null ? v.toFixed(2) : "—"),
        },
      ]}
    />
  )
}

export default function OrderDetailDrawer({ order, open, onClose }) {
  if (!order) return null

  const sp = order.selectedProduct || {}
  const print = sp.print || {}
  const isFixed = sp.advanced?.fixedPrice
  const priceRows = isFixed ? sp.dynamicFixedPrice : sp.dynamicPrice
  const status = order.orderStatus
  const statusColor = STATUS_COLOR[status] || "default"

  return (
    <Drawer
      title={
        <span>
          {order.orderNumber}{" "}
          <Tag color={statusColor} style={{ marginLeft: 8, fontSize: 12 }}>
            {OrderLocation(status) || status}
          </Tag>
        </span>
      }
      width={520}
      placement="right"
      open={open}
      onClose={onClose}
      destroyOnClose>

      {/* Technical image */}
      {sp.technicalPage && (
        <>
          <div style={{ textAlign: "center", marginBottom: 16 }}>
            <Image
              src={sp.technicalPage}
              height={120}
              style={{ objectFit: "contain", borderRadius: 6 }}
              fallback="https://via.placeholder.com/200x120?text=No+Image"
            />
          </div>
        </>
      )}

      {/* Main info */}
      <Divider orientation="left" orientationMargin={0} style={{ fontSize: 12 }}>Order Info</Divider>
      <Descriptions bordered size="small" column={2}>
        <Descriptions.Item label="Order #">{order.orderNumber || "—"}</Descriptions.Item>
        <Descriptions.Item label="Type">{order.orderType || "—"}</Descriptions.Item>
        <Descriptions.Item label="Status">
          <Badge color={statusColor} text={OrderLocation(status) || status} />
        </Descriptions.Item>
        <Descriptions.Item label="Manager">{order.user || "—"}</Descriptions.Item>
        <Descriptions.Item label="Created">{fmt(order.created)}</Descriptions.Item>
        <Descriptions.Item label="Due">{fmt(order.orderDueDate)}</Descriptions.Item>
        {order.completedTime && (
          <Descriptions.Item label="Completed" span={2}>{fmt(order.completedTime)}</Descriptions.Item>
        )}
      </Descriptions>

      {/* Product info */}
      <Divider orientation="left" orientationMargin={0} style={{ fontSize: 12, marginTop: 16 }}>Product</Divider>
      <Descriptions bordered size="small" column={2}>
        <Descriptions.Item label="Name" span={2}>
          {sp.product?.productName || "—"}
        </Descriptions.Item>
        <Descriptions.Item label="Machine">{returnProperMachineTag(sp.machine)}</Descriptions.Item>
        <Descriptions.Item label="Material">{sp.material || "—"}</Descriptions.Item>
        <Descriptions.Item label="Width">{sp.labelWidth ? `${sp.labelWidth} mm` : "—"}</Descriptions.Item>
        <Descriptions.Item label="Height">{sp.labelHeight ? `${sp.labelHeight} mm` : "—"}</Descriptions.Item>
        <Descriptions.Item label="Knife">{sp.knifeCode || "—"}</Descriptions.Item>
        <Descriptions.Item label="Columns">{sp.numberOfColumns || "—"}</Descriptions.Item>
        <Descriptions.Item label="Qty ordered">
          {sp.machine === "Tape"
            ? `${order.rollCount?.toLocaleString() || "—"} rolls`
            : `${order.labelCount?.toLocaleString() || "—"} pcs`}
        </Descriptions.Item>
        <Descriptions.Item label="Unit price">
          {order.price != null ? `€${order.price}` : "—"}
        </Descriptions.Item>
      </Descriptions>

      {/* Print info */}
      {print && Object.keys(print).length > 0 && (
        <>
          <Divider orientation="left" orientationMargin={0} style={{ fontSize: 12, marginTop: 16 }}>Print</Divider>
          <Descriptions bordered size="small" column={2}>
            <Descriptions.Item label="CMYK">{print.cmykColors ?? "—"}</Descriptions.Item>
            <Descriptions.Item label="Pantone">{print.pantoneColors ?? "—"}</Descriptions.Item>
            <Descriptions.Item label="White">{print.coveringWCount ?? "—"}</Descriptions.Item>
            <Descriptions.Item label="Gold/Silver">{print.goldOrSilver ?? "—"}</Descriptions.Item>
            <Descriptions.Item label="Varnish">{print.varnishType || "—"}</Descriptions.Item>
            <Descriptions.Item label="Foil">{print.foilCount ?? "—"}</Descriptions.Item>
          </Descriptions>
        </>
      )}

      {/* Price list */}
      <Divider orientation="left" orientationMargin={0} style={{ fontSize: 12, marginTop: 16 }}>
        Price List {isFixed ? <Tag color="red" style={{ fontSize: 10 }}>Fixed</Tag> : <Tag color="blue" style={{ fontSize: 10 }}>Dynamic</Tag>}
      </Divider>
      {priceRows?.length ? (
        <PriceTable rows={priceRows} />
      ) : (
        <Empty description="No price list" image={Empty.PRESENTED_IMAGE_SIMPLE} />
      )}

      {/* Machine completion notes */}
      {(order.machineCompletion?.finishComment || order.machineIssues?.issue) && (
        <>
          <Divider orientation="left" orientationMargin={0} style={{ fontSize: 12, marginTop: 16 }}>Completion Notes</Divider>
          <Descriptions bordered size="small" column={1}>
            {order.machineCompletion?.finishComment && (
              <Descriptions.Item label="Comment">{order.machineCompletion.finishComment}</Descriptions.Item>
            )}
            {order.machineIssues?.issue && (
              <Descriptions.Item label="Issue">{order.machineIssues.issueComment || "Yes"}</Descriptions.Item>
            )}
          </Descriptions>
        </>
      )}
    </Drawer>
  )
}
